import { getRelayEnvironment } from '@confluence/graphql-provider/entry-points/relayEnvironment.ts';
import { createIdentifierFromQuery } from '@confluence/graphql/entry-points/createIdentifierFromQuery.ts';
import { getClientStream } from '@confluence/ssr-data-streaming/entry-points/client.ts';
import { expVal } from '@confluence/feature-experiments';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';

import ConversationQueryArtifact, {
	type ObjectCommentContainerPreloadObjectCommentsQuery$variables as ConversationQuery$variables,
} from './components/__generated__/ObjectCommentContainerPreloadObjectCommentsQuery.graphql.ts';
import { getOrCreateConversationQueryOwner } from './ConversationQueryOwner.ts';

declare global {
	interface Window {
		__CONVERSATION_QUERY_VARIABLES__?: ConversationQuery$variables;
	}
}

type RelayStreamEvent = {
	type: string;
	operation: string;
	variables: Record<string, unknown>;
};

/**
 * Called during browser bootstrap after the Relay environment and Statsig are initialized.
 * SSR transfers records and variables; query references always belong to the client environment.
 */
export function initializeConversationQueryHydration(): void {
	if (
		StatsigConfigurations.isKillSwitchOn('confluence_vp_conversation_preloaded_query') ||
		!expVal('confluence_exp_conversation_contract', 'isEnabled', false)
	) {
		return;
	}
	const environment = getRelayEnvironment();
	const owner = getOrCreateConversationQueryOwner(environment);

	if (window.__CONVERSATION_QUERY_VARIABLES__) {
		owner.preload(window.__CONVERSATION_QUERY_VARIABLES__);
	}
	if (!window.__SSR_REACT_STREAMING__) return;

	// The existing Relay network listener caches each response before this listener runs.
	// loadQuery can therefore consume the streamed response without a browser request, even
	// when the initial __RELAY_STORE__ snapshot preceded this boundary's response.
	const { text, id } = ConversationQueryArtifact.params;
	const operation = text ? createIdentifierFromQuery(text) : `id:${id}`;
	const stream = getClientStream<RelayStreamEvent>('relay');
	const onResult = (event: RelayStreamEvent) => {
		if (event.type !== 'result' || event.operation !== operation) return;
		const { pageId } = event.variables;
		if (typeof pageId !== 'string') return;
		owner.preload({ ...event.variables, pageId });
	};
	stream.addListener(onResult);
	owner.setHydrationCleanup(() => stream.removeListener(onResult));
}
